/**
 * Primitivas v2 (extraídas de MetaOverallV2/components/ui.tsx) — medidas idênticas aos protótipos, cores via tokens.
 * Usadas por ROI v2, Google Ads v2, Dashboard v2 e WhatsApp v2.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { avatarBg, hueOf, seg, v } from './theme';
import { useDismiss } from './useDismiss';
import { useFocusTrap } from './useFocusTrap';

/* ----------------------------------------------------------- Segmented */
export interface SegOpt<T extends string | number> { k: T; l: ReactNode; icon?: ReactNode }

/** Grupo segmentado (container 10px/pad 3, itens 26px/radius 7). `bg` = fundo do container. */
export function Segmented<T extends string | number>({ opts, value, onChange, bg = v('surface'), size = 'md', style }: {
    opts: SegOpt<T>[]; value: T; onChange: (k: T) => void; bg?: string; size?: 'md' | 'sm'; style?: CSSProperties;
}) {
    const h = size === 'sm' ? 24 : 26;
    return (
        <div style={{ display: 'flex', gap: 2, padding: 3, background: bg, border: `1px solid ${v('border')}`, borderRadius: 10, ...style }}>
            {opts.map((o) => (
                <button key={String(o.k)} type="button" onClick={() => onChange(o.k)}
                    style={{ height: h, padding: '0 10px', border: 0, borderRadius: 7, ...seg(o.k === value), fontSize: 12.5, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {o.icon}{o.l}
                </button>
            ))}
        </div>
    );
}

/* ----------------------------------------------------------- Toggle */
/**
 * Chave única das telas v2.
 *   - `sm` (padrão, ROI/Google Ads/Dashboard): 30×18, knob 14.
 *   - `md` (WhatsApp v2, medida do mockup — D10): 34×20, raio 10, padding 3, knob 14; desligado = trilho `track`, knob `faint`.
 * `label` vira `role="switch"` + `aria-label`; `disabled` deixa 40% e ignora o clique.
 */
export function Toggle({ on, onChange, size = 'sm', disabled, label, title }: {
    on: boolean; onChange: () => void; size?: 'sm' | 'md'; disabled?: boolean; label?: string; title?: string;
}) {
    const md = size === 'md';
    const w = md ? 34 : 30, h = md ? 20 : 18, pad = md ? 3 : 2;
    return (
        <button type="button" onClick={disabled ? undefined : onChange} disabled={disabled}
            {...(label ? { role: 'switch', 'aria-checked': on, 'aria-label': label } : { 'aria-pressed': on })}
            title={title ?? label}
            style={{ position: 'relative', width: w, height: h, flexShrink: 0, borderRadius: h / 2, border: 0, padding: 0, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
                background: on ? v('text') : md ? v('border-2') : v('border-3') }}>
            <span style={{ position: 'absolute', top: pad, left: on ? w - 14 - pad : pad, width: 14, height: 14, borderRadius: 7, background: on ? v('bg') : md ? v('faint') : v('muted') }} />
        </button>
    );
}

/* ----------------------------------------------------------- Checkbox numerado */
/** Quadrado 18px radius 5, com número (ordem) ou ✓. */
export function Check({ on, mark, fontSize }: { on: boolean; mark?: string; fontSize?: number }) {
    return (
        <span style={{ width: 18, height: 18, flexShrink: 0, borderRadius: 5, border: `1px solid ${on ? v('text') : v('line')}`, background: on ? v('text') : 'transparent', color: v('bg'), fontSize: fontSize ?? (mark && mark !== '✓' ? 11 : 12), fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {on ? mark ?? '✓' : ''}
        </span>
    );
}

/* ----------------------------------------------------------- Avatar */
const AVATAR_FONT: Record<number, number> = { 30: 12, 34: 13, 40: 14, 46: 17 };
/** Avatar com foto (`url`) ou inicial sobre um fundo de matiz estável pelo id. */
export function Avatar({ id, name, size = 40, url, radius }: { id: string; name: string; size?: number; url?: string; radius?: number }) {
    const [broken, setBroken] = useState(false);
    const hue = hueOf(id || name);
    const r = radius ?? Math.round(size / 2);
    if (url && !broken) {
        return <img src={url} alt="" onError={() => setBroken(true)} style={{ width: size, height: size, borderRadius: r, objectFit: 'cover', flexShrink: 0, background: avatarBg(hue) }} />;
    }
    return (
        <span style={{ width: size, height: size, borderRadius: r, flexShrink: 0, background: avatarBg(hue), color: v('text'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: AVATAR_FONT[size] ?? Math.round(size * 0.36), fontWeight: 600 }}>
            {(name || '?').trim().charAt(0).toUpperCase()}
        </span>
    );
}

/* ----------------------------------------------------------- Spark */
export function Spark({ line, area, stroke, fill, w = 100, h = 28, width = '100%', height, sw = 1.5 }: {
    line: string; area?: string; stroke: string; fill?: string; w?: number; h?: number; width?: number | string; height: number | string; sw?: number;
}) {
    return (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width, height, display: 'block' }}>
            {area && fill && <path d={area} fill={fill} />}
            <path d={line} fill="none" stroke={stroke} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

/* ----------------------------------------------------------- Drawer (direita) */
/**
 * `trapFocus` (padrão desligado): o painel vira um diálogo modal (role/aria-modal/aria-label), o foco entra nele
 * (`[data-autofocus]` ou o 1º focável), fica preso (Tab/Shift+Tab), volta para quem abriu ao fechar, e Esc chama `onClose`.
 */
export function Drawer({ open, onClose, width = 420, overlay = 0.55, title, sub, children, footer, trapFocus = false, ariaLabel }: {
    open: boolean; onClose: () => void; width?: number; overlay?: number; title: ReactNode; sub?: ReactNode; children: ReactNode; footer?: ReactNode;
    trapFocus?: boolean; ariaLabel?: string;
}) {
    const panel = useRef<HTMLElement>(null);
    const onTrapKey = useFocusTrap(open && trapFocus, panel);
    useEffect(() => {
        if (!open || !trapFocus) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, trapFocus, onClose]);
    if (!open) return null;
    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: `rgb(var(--rv-overlay) / ${overlay})` }} />
            <aside ref={panel} className="rv"
                {...(trapFocus ? { role: 'dialog', 'aria-modal': true, 'aria-label': ariaLabel, tabIndex: -1, onKeyDown: onTrapKey } : {})}
                style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 71, width: `min(${width}px, 100%)`, background: v('modal'), borderLeft: `1px solid ${v('border')}`, display: 'flex', flexDirection: 'column', outline: 'none' }}>
                <div style={{ padding: '20px 22px', borderBottom: `1px solid ${v('border')}`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
                        {sub && <div style={{ fontSize: 12.5, color: v('muted'), marginTop: 4, lineHeight: 1.45 }}>{sub}</div>}
                    </div>
                    <CloseBtn onClick={onClose} size={30} />
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px 22px', display: 'flex', flexDirection: 'column', gap: 26 }}>{children}</div>
                {footer && <div style={{ padding: '14px 22px', borderTop: `1px solid ${v('border')}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>{footer}</div>}
            </aside>
        </>
    );
}

/* ----------------------------------------------------------- Modal (centro) */
export function Modal({ open, onClose, maxWidth, z = 60, overlay = 0.72, padding = '32px 24px', radius = 20, children, panelStyle }: {
    open: boolean; onClose: () => void; maxWidth: number; z?: number; overlay?: number; padding?: string; radius?: number; children: ReactNode; panelStyle?: CSSProperties;
}) {
    if (!open) return null;
    return (
        <div className="rv rv-modal-wrap" onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: z, background: `rgb(var(--rv-overlay) / ${overlay})`, backdropFilter: 'blur(6px)', padding, overflowY: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
            <div onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', maxWidth, background: v('modal'), border: `1px solid ${v('border')}`, borderRadius: radius, ...panelStyle }}>
                {children}
            </div>
        </div>
    );
}

export function CloseBtn({ onClick, size = 34 }: { onClick: () => void; size?: number }) {
    return (
        <button type="button" onClick={onClick} className="rv-h-text" aria-label="Fechar"
            style={{ width: size, height: size, flexShrink: 0, borderRadius: 9, border: `1px solid ${v('border-2')}`, background: v('chip'), color: v('muted'), cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
            ×
        </button>
    );
}

/* ----------------------------------------------------------- Popover ancorado */
/**
 * Popover absoluto (top 40, right 0) ancorado no pai `position: relative`. Fecha ao tocar fora do pai (gatilho +
 * painel), com Esc e pelo gatilho — via `useDismiss` (sem o antigo fundo invisível `fixed inset 0`).
 * `trigger`: para onde o foco volta no Esc (padrão: quem tinha o foco ao abrir).
 */
export function AnchoredPopover({ open, onClose, width = 260, children, style, trigger }: {
    open: boolean; onClose: () => void; width?: number; children: ReactNode; style?: CSSProperties; trigger?: RefObject<HTMLElement | null>;
}) {
    const panel = useRef<HTMLDivElement>(null);
    const parent = { get current() { return panel.current?.parentElement ?? null; } };
    useDismiss(open, onClose, { refs: [parent, panel], trigger });
    if (!open) return null;
    return (
        <div ref={panel} style={{ position: 'absolute', top: 40, right: 0, zIndex: 31, width, background: v('popover'), border: `1px solid ${v('border-3')}`, borderRadius: 12, padding: 8, boxShadow: 'var(--rv-shadow)', display: 'flex', flexDirection: 'column', gap: 2, ...style }}>
            {children}
        </div>
    );
}

/** Linha de opção de popover (34px, radius 8). */
export function PopRow({ on, onClick, children, right }: { on?: boolean; onClick: () => void; children: ReactNode; right?: ReactNode }) {
    return (
        <button type="button" onClick={onClick} className={on ? '' : 'rv-h-hover'}
            style={{ height: 34, padding: '0 10px', border: 0, borderRadius: 8, ...seg(!!on), fontSize: 13, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, ...(on ? {} : { color: v('text-3') }) }}>
            <span>{children}</span>
            {right != null && <span style={{ opacity: 0.6, fontSize: 12 }}>{right}</span>}
        </button>
    );
}

export const PopLabel = ({ children }: { children: ReactNode }) => (
    <div style={{ fontSize: 11.5, color: v('faint'), padding: '6px 10px 4px' }}>{children}</div>
);
export const PopDivider = () => <div style={{ height: 1, background: v('border-2'), margin: '6px 4px' }} />;

/* ----------------------------------------------------------- Botões */
/** Botão secundário 32px radius 9 (ex.: "Personalizar", "Filtros"). */
export function GhostBtn({ onClick, children, active, style }: { onClick: () => void; children: ReactNode; active?: boolean; style?: CSSProperties }) {
    return (
        <button type="button" onClick={onClick} className="rv-h-btn"
            style={{ height: 32, padding: '0 12px', borderRadius: 9, border: `1px solid ${active ? v('line') : v('border')}`, background: active ? v('track') : v('surface'), color: v('text-2'), fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', ...style }}>
            {children}
        </button>
    );
}

/** Botão primário (fundo = texto). */
export function PrimaryBtn({ onClick, children, style }: { onClick: () => void; children: ReactNode; style?: CSSProperties }) {
    return (
        <button type="button" onClick={onClick}
            style={{ height: 34, padding: '0 14px', border: 0, borderRadius: 10, background: v('text'), color: v('bg'), fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', ...style }}>
            {children}
        </button>
    );
}

/* ----------------------------------------------------------- Origem */
/** Rótulo de origem (10px/600 uppercase .06em). */
export const OriginTag = ({ label, color, size = 10 }: { label: string; color: string; size?: number }) => (
    <span style={{ fontSize: size, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color }}>{label}</span>
);

/** Estado vazio / erro no padrão da tela. */
export function Empty({ children }: { children: ReactNode }) {
    return <div style={{ padding: '28px 18px', textAlign: 'center', color: v('faint'), fontSize: 13 }}>{children}</div>;
}

/** Skeleton simples. */
export function Skel({ h, w = '100%', r = 14 }: { h: number; w?: number | string; r?: number }) {
    return <div style={{ height: h, width: w, borderRadius: r, background: v('surface'), border: `1px solid ${v('border')}`, opacity: 0.7 }} className="animate-pulse" />;
}
